import { Box, Button, FormField, Heading, TextButton } from '@wix/design-system'
import { ArrowRight, ChevronLeft, Search } from '@wix/wix-ui-icons-common'
import React, { FC, useEffect, useMemo } from 'react'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { usePageLogger } from '../../../bi/mfi'
import { BusinessTermService } from '../../reusable/Category/api/BusinessTermService'
import { AutoCompleteWithOverrides } from '../../reusable/Category/AutoCompleteWithOverrides'
import { CategoryLayout } from '../../reusable/Category/constants'
import { useCategory } from '../../reusable/Category/useCategory'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import { FancyAutoComplete } from '../../reusable/FancyAutoComplete'
import Close from '../../reusable/icons/Close.svg'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import s from './CategoryNew.scss'
import { useScrollbarBehavior } from './useScrollbarBehavior'

export type CategoryNewProps = {
  layout?: CategoryLayout
}

export const CategoryNew: FC<CategoryNewProps> = props => {
  const pageLogger = usePageLogger()
  const { onScroll } = useScrollbarBehavior()

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const businessTermService = useMemo(() => new BusinessTermService(ambassadorHttpClient), [])
  const {
    autoCompleteRef,
    categoriesOptions,
    handlers: {
      handleClear,
      handleSearchSelect,
      handleInputClick,
      handleSearchChange,
      handleNextClick,
      handleSuggestionClick,
      handleBackClick,
      handleSkipClick,
    },
    state: { search = '', category, categoriesLoading },
    content: {
      placeholder,
      isAddUserInputAsOption,
      title,
      suggestionsLabel,
      suggestions,
      nextButton,
      skipButton,
      video: contentVideo,
    },
    selectedId,
  } = useCategory({ ...props, businessTermService })

  const prefixNode = useMemo(
    () => (
      <Box marginLeft="12px" height="100%" verticalAlign="middle">
        <Search className={s.searchIcon} />
      </Box>
    ),
    [],
  )

  const suffixNode = useMemo(
    () =>
      search ? (
        <Box height="100%" verticalAlign="middle">
          <button className={s.CategoryNew__clearBtn} onClick={categoriesLoading ? undefined : handleClear}>
            <Close className={s.CategoryNew__closeIcon} />
          </button>
        </Box>
      ) : null,
    [categoriesLoading, handleClear, search],
  )

  return (
    <DesignSystemTokensLoader type="odeditor">
      <Box className={s.CategoryNew} direction="vertical">
        <header className={s.CategoryNew__header}>
          <button className={s.CategoryNew__backBtn} onClick={handleBackClick}>
            <ChevronLeft />
          </button>
          <hr className={s.CategoryNew__divider} />
          <WixLogo width={46} height={18} />
        </header>

        <div className={s.CategoryNew__content}>
          <Box display="flex" align="center" paddingBottom="42px" paddingTop="78px">
            <div className={s.CategoryNew__contentLeft} onScroll={onScroll}>
              <Heading className={s.CategoryNew__title}>{title}</Heading>
              <div className={s.CategoryNew__formField}>
                <FormField>
                  <FancyAutoComplete
                    maxHeightPixels={290}
                    prefixNode={prefixNode}
                    suffix={suffixNode}
                    popoverProps={{ moveBy: { y: 2 } }}
                    ref={autoCompleteRef}
                    value={search}
                    selectedId={selectedId}
                    onChange={handleSearchChange}
                    status={categoriesLoading ? 'loading' : undefined}
                    options={categoriesOptions}
                    placeholder={placeholder}
                    size="large"
                    onSelect={handleSearchSelect}
                    onInputClicked={handleInputClick}
                    className={s.CategoryNew__searchField}
                    clearButton={false}
                    border="bottomLine"
                    customRenderer={AutoCompleteWithOverrides}
                    isIconHidden={isAddUserInputAsOption}
                  />
                </FormField>
                <Button
                  size="medium"
                  skin="dark"
                  className={s.CategoryNew__nextButton}
                  onClick={handleNextClick}
                  disabled={!category}
                >
                  {nextButton}
                </Button>
              </div>
              {!category && (
                <div className={s.CategoryNew__listContainer}>
                  <span className={s.CategoryNew__listTitle}>{suggestionsLabel}</span>
                  <ul className={s.CategoryNew__list}>
                    {suggestions.items.map(({ text }, i) => (
                      <li key={i}>
                        <button className={s.CategoryNew__listItem} onClick={() => handleSuggestionClick(text, i)}>
                          {text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Box>
          <div className={s.CategoryNew__right}>
            <div className={s.CategoryNew__videoContainer}>
              {contentVideo && <video className={s.CategoryNew__video} autoPlay muted src={contentVideo} />}
            </div>
          </div>
        </div>

        <div className={s.CategoryNew__skip}>
          <TextButton onClick={handleSkipClick} skin="dark" suffixIcon={<ArrowRight />}>
            {skipButton}
          </TextButton>
        </div>
      </Box>
    </DesignSystemTokensLoader>
  )
}
